import React, { Component } from 'react';
import moment from 'moment';
import { AgChartsReact } from 'ag-charts-react';

import { torGridBALService } from '../../bal/torGridBALService.bal';
import TorGrid from './../torgrid/torgrid';

class CashMargin extends Component {
    constructor(props) {
        super(props);
        this.state = {
            chartData: []
        }
    }

    getHistoricalCashMarginQuery = () => {
        const { AsOfDate } = this.props;
        return AsOfDate ? `Exec [sp_get_historical_cash_margin] '${AsOfDate}'` : `Exec [sp_get_historical_cash_margin]`;
    }

    loadChartData = () => {
        torGridBALService.getRowData('hedgeReport', { query: this.getHistoricalCashMarginQuery() }).then(data => {
            this.setState({ chartData: data ?? [] });
        });
    }

    getReportDateMoment = (value) => {
        const date = moment(value, [moment.ISO_8601, 'YYYY-MM-DD', 'MM/DD/YYYY', 'YYYYMMDD'], true);
        return date.isValid() ? date : moment(value);
    }

    getFilteredChartData = () => {
        const { chartData } = this.state;
        const datedRows = (chartData ?? []).filter(row => this.getReportDateMoment(row['For Report Date']).isValid());

        if (datedRows.length === 0) {
            return [];
        }

        const latestDate = datedRows.reduce((latest, row) => {
            const rowDate = this.getReportDateMoment(row['For Report Date']);
            return rowDate.isAfter(latest) ? rowDate : latest;
        }, this.getReportDateMoment(datedRows[0]['For Report Date']));

        const cutoffDate = latestDate.clone().subtract(2, 'years').startOf('day');

        return datedRows.filter(row => {
            const reportDate = this.getReportDateMoment(row['For Report Date']);
            return reportDate.isSameOrAfter(cutoffDate);
        });
    }

    getChartOptions = () => {
        const filteredChartData = this.getFilteredChartData();
        const preferredFundOrder = ['TOR', 'TIFO', 'TACO', 'TACO II', 'TACO III', 'TACO IV', 'TALC'];
        const getBaseFund = fund => fund.replace(/\s+VCC$/i, '').trim();
        const discoveredFunds = [...new Set(filteredChartData.map(row => row.fund).filter(Boolean).map(getBaseFund))];
        const orderedFunds = [
            ...preferredFundOrder.filter(fund => discoveredFunds.includes(fund)),
            ...discoveredFunds.filter(fund => !preferredFundOrder.includes(fund))
        ];

        const groupedByDate = {};
        filteredChartData.forEach(row => {
            const reportDate = this.getReportDateMoment(row['For Report Date']);
            if (!reportDate.isValid() || !row.fund) {
                return;
            }

            const dateKey = reportDate.format('YYYY-MM-DD');
            const fund = getBaseFund(row.fund);
            if (!groupedByDate[dateKey]) {
                groupedByDate[dateKey] = {
                    reportDate: reportDate.toDate()
                };
            }

            groupedByDate[dateKey][fund] = (groupedByDate[dateKey][fund] ?? 0) + (row.Margin ?? 0);
        });

        const chartRows = Object.keys(groupedByDate)
            .sort((left, right) => new Date(left) - new Date(right))
            .map(dateKey => groupedByDate[dateKey]);

        const chartPalette = ['#0f4c81', '#c05a00', '#2e8b57', '#7a3fc4', '#008b8b', '#b8860b', '#c94c4c'];

        return {
            autoSize: true,
            background: {
                fill: '#ffffff'
            },
            padding: {
                top: 24,
                right: 24,
                bottom: 16,
                left: 12
            },
            data: chartRows,
            title: {
                text: 'Historical Margin',
                fontSize: 20,
                fontWeight: '600',
                color: '#183153'
            },

            series: orderedFunds.map((fund, index) => ({
                type: 'line',
                xKey: 'reportDate',
                yKey: fund,
                yName: fund,
                stroke: chartPalette[index % chartPalette.length],
                strokeWidth: 2.5,
                marker: {
                    enabled: false,
                    fill: chartPalette[index % chartPalette.length],
                    stroke: chartPalette[index % chartPalette.length]
                },
                highlightStyle: {
                    fill: chartPalette[index % chartPalette.length],
                    stroke: chartPalette[index % chartPalette.length]
                },
                tooltip: {
                    renderer: params => ({
                        title: params.yName,
                        content: `${moment(params.xValue).format('MMM DD, YYYY')} : ${(params.yValue ?? 0).toFixed(2).replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",")}`
                    })
                }
            })),
            axes: [
                {
                    type: 'time',
                    position: 'bottom',
                    nice: true,
                    tick: { count: 8 },
                    label: {
                        format: '%b %Y',
                        color: '#5f6f86',
                        rotation: 0
                    },
                    line: {
                        color: '#d8e1ee'
                    },
                    gridStyle: [
                        {
                            stroke: '#eef3f9',
                            lineDash: [4, 4]
                        }
                    ]
                },
                {
                    type: 'number',
                    position: 'left',
                    nice: true,
                    tick: { count: 6 },
                    label: {
                        color: '#5f6f86',
                        formatter: params => `${(params.value / 1e6).toFixed(0)}M`
                    },
                    line: {
                        color: '#d8e1ee'
                    },
                    gridStyle: [
                        {
                            stroke: '#eef3f9',
                            lineDash: [4, 4]
                        }
                    ]
                }
            ],
            legend: {
                enabled: true,
                position: 'bottom',
                item: {
                    label: {
                        color: '#42556d'
                    }
                }
            }
        };
    }

    render() {
        return this.getTemplate();
    }

    componentDidMount() {
        this.loadChartData();
    }

    componentDidUpdate(prevProps) {
        const { AsOfDate } = this.props;
        if (prevProps.AsOfDate !== AsOfDate) {
            this.loadChartData();
        }
    }


    getTemplate() {
        const { AsOfDate } = this.props;
        return (
            <div className={this.props.containerClassName ? `${this.props.containerClassName} liquidity-content` : "liquidity-content"}>
                <section className="liquidity-panel liquidity-table-panel">
                    <header className="liquidity-panel-header liquidity-panel-header-single">
                        <h3>Cash / Margin Summary</h3>
                    </header>
                    <div className="liquidity-grid-shell">
                        <TorGrid
                            guikey="cashMarginSummary"
                            isHideDateInputs={true}
                            isHideSaveGridLayout={true}
                            simpleLayout={true}
                            disableColumnResize={true}
                            query={AsOfDate ? `Exec [sp_get_historical_cash_margin_summary] '${AsOfDate}'` : `Exec [sp_get_historical_cash_margin_summary]`}
                            gridIdentityColumn="position_id"
                            groupIncludeTotalFooter={true}
                            suppressExpandablePivotGroups={true}
                            className="dashboardgrid liquidity-summary-grid"
                            rowGroupPanelShow="none"
                            enableFilter={false}
                            domLayout="autoHeight"
                            groupDefaultExpanded={-1}

                            groupBy="Fund"

                            defaultColDef={this.props.defaultColDef ??
                            {
                                sortable: false
                                , filter: false
                                , floatingFilter: false
                                , enableRowGroup: true
                                , resizable: false
                                , suppressSizeToFit: false
                            }
                            }

                            handleRowSelected={this.handleRowSelected}
                            sideBar='none'
                            gridTitle=''
                        />
                    </div>
                </section>
                <section className="liquidity-panel liquidity-chart-panel">
                    <header className="liquidity-panel-header">
                        <div className="liquidity-panel-copy">
                            <h3>Historical Margin</h3>
                        </div>
                    </header>
                    <div className="liquidity-chart-shell">
                        <AgChartsReact options={this.getChartOptions()} />
                    </div>
                </section>
            </div>
        );
    }
}
export default CashMargin;